import "./LeagueScheduleCard.css";

import type {
  SportsLeagueRecord,
  SportsScheduleGame,
  SportsTeamSummary,
} from "../../api/sports";

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function formatStage(value: string): string {
  const normalized = String(value || "regular")
    .replace(/[_-]+/g, " ")
    .trim();
  if (!normalized) return "Games";
  return normalized.replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function formatGameTime(value: string | null): string {
  if (!value) return "Time TBD";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Time TBD";

  return new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function teamInitials(team: SportsTeamSummary): string {
  const abbreviation = String(team.abbreviation || "").trim();
  if (abbreviation) return abbreviation.slice(0, 3).toUpperCase();

  return String(team.name || "TBD")
    .split(/\s+/)
    .filter(Boolean)
    .slice(-2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("")
    .slice(0, 3);
}

function renderTeam(team: SportsTeamSummary): string {
  const icon = String(team.icon_url || "").trim();
  const officialUrl = String(team.official_url || "").trim();
  const marker = icon
    ? `<img class="sports-league-team-icon" src="${escapeHtml(icon)}" alt="" loading="lazy" />`
    : `<span class="sports-league-team-marker" aria-hidden="true">${escapeHtml(
        teamInitials(team),
      )}</span>`;

  const teamIdentity = officialUrl
    ? `<a class="sports-league-team-link" href="${escapeHtml(officialUrl)}" target="_blank" rel="noopener noreferrer" aria-label="Open ${escapeHtml(team.name || "team")} official website">${marker}<span class="sports-league-team-name">${escapeHtml(team.name || "TBD")}</span></a>`
    : `${marker}<span class="sports-league-team-name">${escapeHtml(team.name || "TBD")}</span>`;

  const score =
    typeof team.score === "number"
      ? `<strong class="sports-league-team-score">${escapeHtml(team.score)}</strong>`
      : "";

  return `
    <div class="sports-league-team-row">
      <div class="sports-league-team-name-wrap">
        ${teamIdentity}
      </div>
      ${score}
    </div>
  `;
}

function renderGame(game: SportsScheduleGame): string {
  const statusState = String(game.status_state || "").toLowerCase();
  const isFinal = statusState === "final";
  const live = statusState === "in_progress";
  const status = live
    ? "Live"
    : isFinal
      ? "Final"
      : formatGameTime(game.starts_at);

  if (game.display_type === "event") {
    return `
      <article class="sports-league-game sports-league-event" data-game-id="${escapeHtml(game.id)}">
        <div class="sports-league-game-meta">
          <span>${escapeHtml(status)}</span>
        </div>
        <div class="sports-league-event-title">${escapeHtml(game.home_team?.name || "Event")}</div>
        <div class="sports-league-event-secondary">${escapeHtml(game.away_team?.name || "")}</div>
        ${
          game.venue
            ? `<div class="sports-league-game-venue">${escapeHtml(game.venue)}</div>`
            : ""
        }
      </article>
    `;
  }

  return `
    <article class="sports-league-game" data-game-id="${escapeHtml(game.id)}">
      <div class="sports-league-game-meta">
        <span>${escapeHtml(status)}</span>
        ${
          game.week
            ? `<span>Week ${escapeHtml(game.week)}</span>`
            : ""
        }
      </div>
      <div class="sports-league-game-teams">
        ${renderTeam(game.away_team)}
        ${renderTeam(game.home_team)}
      </div>
      ${
        game.venue
          ? `<div class="sports-league-game-venue">${escapeHtml(game.venue)}</div>`
          : ""
      }
    </article>
  `;
}

export function renderLeagueScheduleCard(league: SportsLeagueRecord): string {
  const schedule = league.schedule;
  const games = Array.isArray(schedule?.games) ? schedule!.games : [];
  const grouped = new Map<string, SportsScheduleGame[]>();

  for (const game of games) {
    const key = String(game.stage || "regular");
    const bucket = grouped.get(key) || [];
    bucket.push(game);
    grouped.set(key, bucket);
  }

  const groupsHtml = Array.from(grouped.entries())
    .map(
      ([stage, stageGames]) => `
        <section class="sports-league-stage">
          <h3>${escapeHtml(formatStage(stage))}</h3>
          <div class="sports-league-games">
            ${stageGames.map(renderGame).join("")}
          </div>
        </section>
      `,
    )
    .join("");

  const cacheError = schedule?.cache?.last_sync_error;
  const syncStatus = schedule?.cache?.sync_status || "";
  const access = String(league.schedule_access || "");

  let emptyMessage = "No cached games are available for this schedule window yet.";
  let emptyNote = "";

  if (access === "subscription_or_trial" || access === "subscription_required") {
    emptyMessage = cacheError
      ? "This schedule endpoint is configured, but the current BALLDONTLIE key did not return game data."
      : "This schedule is configured and will appear after an authorized cache refresh.";
    emptyNote = cacheError
      ? "BALLDONTLIE controls game/match access separately from the sport being listed in the dashboard."
      : "Run the AyeSearch schedule sync command when this API key has game/match access.";
  } else if (syncStatus === "provider_error" || syncStatus === "provider_error_cooldown") {
    emptyNote = "AyeSearch kept the cached result and will retry after the provider cooldown.";
  } else if (cacheError) {
    emptyNote = "Provider refresh will be retried automatically.";
  }

  const emptyState = `
    <div class="sports-league-empty">
      ${escapeHtml(emptyMessage)}
      ${
        emptyNote
          ? `<span class="sports-league-cache-note">${escapeHtml(emptyNote)}</span>`
          : ""
      }
    </div>
  `;

  return `
    <article class="sports-league-card" data-sports-league="${escapeHtml(league.slug)}">
      <header class="sports-league-card-header">
        <div>
          <div class="sports-league-kicker">AYESEARCH SPORTS</div>
          <h1>${escapeHtml(league.short_name || league.name)}</h1>
          <p>${escapeHtml(schedule?.season_display_name || `${league.current_season ?? ""} Season`.trim())}</p>
        </div>
        <div class="sports-league-sport-label">${escapeHtml(league.sport.common_name)}</div>
      </header>

      <div class="sports-league-tabs" aria-label="Sports sections">
        <span class="sports-league-tab sports-league-tab-active">Games</span>
      </div>

      ${games.length ? groupsHtml : emptyState}

      <footer class="sports-league-card-footer">
        Schedule data is cached by AyeSearch from its configured sports provider.
      </footer>
    </article>
  `;
}
